import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "admin", "manager", "supervisor", "employee", "client"];

export default function Users() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", tenant_id: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: c }] = await Promise.all([api.get("/users"), api.get("/companies")]);
      setUsers(u); setCompanies(c);
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error("Failed to load users");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "employee", tenant_id: me?.tenant_id || "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, tenant_id: u.tenant_id || "" }); setOpen(true); };

  const submit = async () => {
    setLoading(true);
    try {
      if (editing) {
        const patch = { name: form.name, role: form.role };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${editing.user_id}`, patch);
        toast.success("User updated");
      } else {
        const payload = { ...form };
        if (!payload.tenant_id) delete payload.tenant_id;
        if (!payload.password) delete payload.password;
        const { data } = await api.post("/users", payload);
        if (data._temp_password) toast.success(`User created. Temp password: ${data._temp_password}`);
        else toast.success("User created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <DashboardLayout
      title={t("users.title")}
      subtitle={t("users.subtitle")}
      right={
        <button data-testid="users-new" onClick={openNew} className="btn-scale h-9 px-3 rounded-md bg-white text-zinc-950 font-medium text-sm flex items-center gap-2 hover:bg-zinc-100">
          <Plus className="w-4 h-4" /> {t("users.new")}
        </button>
      }
    >
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">{t("common.name")}</th>
              <th className="p-3">{t("common.email")}</th>
              <th className="p-3">{t("common.role")}</th>
              <th className="p-3">Tenant</th>
              <th className="p-3">{t("common.status")}</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{u.name}</td>
                <td className="p-3 text-zinc-400">{u.email}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest text-indigo-400">{u.role}</span></td>
                <td className="p-3 text-zinc-500 text-xs">{companies.find(c => c.company_id === u.tenant_id)?.name || (u.tenant_id ? u.tenant_id.slice(-8) : "—")}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-2 text-xs text-zinc-300">
                    <span className={`w-2 h-2 rounded-full ${u.active ? "bg-emerald-500 glow-green" : "bg-zinc-500"}`} />
                    {u.active ? t("common.active") : t("common.inactive")}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`edit-user-${u.user_id}`} onClick={() => openEdit(u)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u.user_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">{t("common.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("users.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2">
            <label><span className="overline block mb-1.5">{t("common.name")}</span>
              <input data-testid="user-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">{t("common.email")}</span>
              <input data-testid="user-field-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm disabled:opacity-60" />
            </label>
            <label><span className="overline block mb-1.5">{t("common.password")} {editing && "(leave blank to keep)"}</span>
              <input data-testid="user-field-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">{t("common.role")}</span>
              <select data-testid="user-field-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {ROLES.filter(r => me?.role === "super_admin" || r !== "super_admin").map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            {me?.role === "super_admin" && (
              <label><span className="overline block mb-1.5">Tenant</span>
                <select data-testid="user-field-tenant" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                  <option value="">— (platform / super_admin)</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </label>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="user-save" disabled={loading} onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-white text-zinc-950 text-sm font-medium">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
